<template>
  <div class="w-full transition-all duration-300">
    <div
      v-if="period.is_booked === false"
      class="w-full border rounded px-1.5 flex flex-col justify-center gap-1 transition-all duration-300 cursor-pointer"
      :class="{
        'pt-4': isFirst,
        'pointer-events-none': !currentUser.role.includes('admin'),
        'hover:bg-hover-state': !isSelected
      }"
      :style="{
        backgroundColor: period.element.backgroundColor,
        borderColor: period.element.borderColor,
        height: period.element.height
      }"
      title="Make appointment"
      @click="onMakeAppointment"
    >
      <BlTextBody2
        :text="`${period.start_time_string} - ${period.end_time_string}`"
        class="transition-all duration-300"
        :style="{ color: period.element.color }"
      />
    </div>

    <div
      v-if="period.is_booked"
      class="relative border rounded px-1.5 transition-all duration-300"
      :class="{
        'pt-4': isFirst,
        'border-dashed': period.appointment.status.includes('booking')
      }"
      :style="{
        backgroundColor: period.element.backgroundColor,
        borderColor: period.element.borderColor,
        height: period.element.height
      }"
      @click="openAppointmentDetails"
    >
      <div
        class="flex h-full cursor-pointer"
        :class="{
          'items-center justify-between gap-1': isCompact && period.count === 1,
          'flex-col justify-center gap-0.5': !isCompact || period.count > 1
        }"
      >
        <BlIcon
          v-if="isEmptyTherapist && !isCompact"
          icon="warning"
          color="tomato-7"
          class="w-6 h-6"
        />
        <BlTextBody2
          v-if="
            !isEmptyTherapist && isOpenRoom && (!isCompact || period.count > 1)
          "
          :text="period.appointment.user.name"
          :style="{ color: period.element.color }"
          class="truncate"
        />

        <div
          class="flex items-center justify-between gap-1 truncate"
          :class="{ 'w-full': isEmptyTherapist && isCompact }"
        >
          <BlTextSubtitle3
            v-if="period.appointment.client && period.appointment.client.name"
            :text="period.appointment.client.name"
            :style="{ color: period.element.color }"
            class="truncate"
          />
          <BlIcon
            v-if="isEmptyTherapist && isCompact"
            icon="warning"
            color="tomato-7"
            class="w-6 h-6"
          />
        </div>

        <BlTextBody2
          v-if="isEmptyTherapist && (!isCompact || period.count > 1)"
          text="Please review"
          :style="{ color: period.element.color }"
        />

        <BlTextBody2
          v-if="!isEmptyTherapist && isCompact && period.count === 1"
          :text="period.appointment.start_time_string"
          :style="{ color: period.element.color }"
        />
        <BlTextBody2
          v-if="!isEmptyTherapist && (!isCompact || period.count > 1)"
          :text="
            `${period.appointment.start_time_string} - ${period.appointment.end_time_string}`
          "
          :style="{ color: period.element.color }"
        />
        <BlTextBody2
          v-if="
            !isEmptyTherapist &&
              !isCompact &&
              enableBranch &&
              period.appointment.room.branch
          "
          :text="period.appointment.room.branch.name"
          :style="{ color: period.element.color }"
          class="truncate"
        />
      </div>

      <div
        v-if="
          detailsOpened &&
            period.appointment &&
            period.appointment.status.includes('booking')
        "
        :data-id="dataId"
        class="absolute top-0 flex flex-col flex-shrink-0 w-64 gap-1 mx-2 overflow-y-auto border rounded shadow-md bg-pure-white border-grey-200"
        :class="[period.is_last_column ? 'right-full' : 'left-full']"
        :style="{ zIndex: 2 }"
      >
        <div
          :data-id="dataId"
          class="flex items-center justify-between flex-shrink-0 w-full h-8 px-3 bg-slate-300"
        >
          <router-link :to="`/appointments/details/${period.appointment.id}`">
            <BlTextBody3
              :data-id="dataId"
              :text="period.appointment.status.replaceAll('_', ' ')"
              class="capitalize"
            />
          </router-link>
          <BlIcon icon="close" color="slate-6" class="w-4 h-4" clickable />
        </div>
        <div
          v-if="appointmentLoading"
          :data-id="dataId"
          class="grid h-28 place-items-center"
        >
          <div :data-id="dataId" class="blum-loader-sm"></div>
        </div>
        <div
          v-else-if="!appointmentDetails"
          :data-id="dataId"
          class="grid h-28 place-items-center"
        >
          <BlTextBody2 :data-id="dataId" text="Appointment not found" />
        </div>
        <div
          v-else
          :data-id="dataId"
          class="flex flex-col flex-shrink-0 gap-3 p-3"
        >
          <div :data-id="dataId" class="flex flex-col gap-1">
            <BlTextSubtitle3 :data-id="dataId" text="Appointment in progress" />
            <div :data-id="dataId">
              <BlTextBody2
                :data-id="dataId"
                text="This appointment slot is currently being booked by "
              />
              <BlTextSubtitle3
                v-if="!appointmentDetails.user"
                :data-id="dataId"
                text="Deleted user"
                class="italic"
              />
              <BlTextSubtitle3
                v-else
                :data-id="dataId"
                :text="appointmentDetails.user.name"
              />
              <BlTextBody2
                :data-id="dataId"
                text="and will be reserved until "
              />
              <BlTextSubtitle3
                :data-id="dataId"
                :text="displayDate(appointmentDetails.end_time, 'HH:mm')"
              />
            </div>
          </div>
        </div>
        <div
          v-if="
            currentUser.role.includes('admin') &&
              appointmentDetails &&
              appointmentDetails.user_id === currentUser.id
          "
          :data-id="dataId"
          class="flex items-center justify-center flex-shrink-0 w-full h-12 px-3 border-t border-slate-400 gap-14"
        >
          <div
            v-if="appointmentDetails.status === 'in_progress'"
            :data-id="dataId"
            class="flex justify-center flex-grow py-1 truncate rounded cursor-pointer hover:bg-hover-state"
            title="Resume booking appointment"
            @click="onResumeMakeAppointment"
          >
            <BlTextSubtitle3
              :data-id="dataId"
              text="Resume booking appointment"
            />
          </div>
          <div
            v-if="appointmentDetails.status === 'completed'"
            :data-id="dataId"
            class="flex justify-center flex-grow py-1 truncate rounded"
          >
            <BlTextSubtitle3
              :data-id="dataId"
              text="Please wait, appointment is scheduling..."
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="
        period.appointment && !period.appointment.status.includes('booking')
      "
      :data-id="dataId"
      class="fixed flex-shrink-0 overflow-y-auto transition-all duration-300 border shadow-md outline-none bg-pure-white border-grey-200"
      :style="{
        zIndex: 101,
        top: '7.5rem',
        right: detailsOpened ? '0rem' : ' -28.75rem',
        height: 'calc(100vh - 7.5rem)',
        width: '28.75rem'
      }"
    >
      <div
        v-if="appointmentLoading"
        :data-id="dataId"
        class="grid h-full place-items-center"
      >
        <div :data-id="dataId" class="blum-loader"></div>
      </div>
      <div
        v-else-if="!appointmentDetails"
        :data-id="dataId"
        class="grid h-full place-items-center"
      >
        <BlTextBody2 :data-id="dataId" text="Appointment not found" />
      </div>
      <UpcomingAppointments
        v-else
        :data-id="dataId"
        :id="dataId"
        :appointment="appointmentDetails"
        @after-commit="$emit('after-commit')"
      />
    </div>

    <RescheduleAppointmentOverlay
      v-if="rescheduleOpened && currentUser.role.includes('admin')"
      :appointmentId="period.appointment.id"
      @close-modal="rescheduleOpened = false"
      @after-commit="$emit('after-commit')"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

import RescheduleAppointmentOverlay from "../overlay/RescheduleAppointmentOverlay.vue";
import UpcomingAppointments from "./UpcomingAppointments.vue";

import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextBody3 from "../../BlTextBody3.vue";
import BlIcon from "../../BlIcon.vue";

export default {
  name: "AppointmentPeriod",
  components: {
    RescheduleAppointmentOverlay,
    UpcomingAppointments,
    BlTextSubtitle3,
    BlTextBody2,
    BlTextBody3,
    BlIcon
  },
  props: {
    isFirst: { type: Boolean, default: false },
    isCompact: { type: Boolean, default: false },
    slotHeight: { type: Number, required: true },
    item: { type: Object, required: true },
    isSelected: { type: Boolean, default: false },
    isOpenRoom: { type: Boolean, default: false }
    // @add-slot
    // @resume-booking
    // @after-commit
  },
  data() {
    return {
      detailsOpened: false,
      rescheduleOpened: false,
      appointmentLoading: false,
      appointmentDetails: null
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter"]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    dataId() {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let combination = "";
      for (let i = 0; i < 6; i++) {
        combination += chars[Math.round(Math.random() * 35)];
      }
      return `bl-appointment-details-${combination}`;
    },
    period() {
      const item = { ...this.item };

      const element = {
        height: ``,
        backgroundColor: "",
        borderColor: item.secondary_color,
        color: item.primary_color
      };

      const slot = this.slotHeight * item.count;
      element.height = `calc(${slot}rem - 0.5rem)`;

      if (item.is_booked) {
        element.backgroundColor = item.appointment.secondary_color;
        element.borderColor = item.appointment.secondary_color;
        element.color = item.appointment.primary_color;

        if (item.appointment.status.includes("booking")) {
          element.borderColor = item.appointment.primary_color;
        }

        if (!item.appointment.user) {
          element.backgroundColor = "#FEF3F2"; // tomato-2
          element.borderColor = "#FEF3F2"; // tomato-2
          element.color = "#DD3F30"; // tomato-7
        }
      }

      if (this.isSelected) {
        element.backgroundColor = item.primary_color;
        element.color = item.secondary_color;
      }

      const startDate = item.date;
      return { ...item, element, start_date: startDate };
    },
    isEmptyTherapist() {
      if (!this.period.is_booked) return true;
      const appointment = this.period.appointment;
      return !appointment.user || !appointment.user.name;
    }
  },
  watch: {
    detailsOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandler);
        }, 10);
      }
    }
  },
  mounted() {},
  methods: {
    ...mapActions(["actionGet"]),
    displayDate(date, format = "dddd, DD MMM YYYY") {
      return moment(date).format(format);
    },
    getSlotCount(start, end) {
      const s = start.split(":").map(i => Number(i));
      const e = end.split(":").map(i => Number(i));
      let slots = 0;
      slots += (e[0] - s[0]) * 2;
      if (s[1] === 30) slots -= 1;
      if (e[1] === 30) slots += 1;
      return slots;
    },
    onMakeAppointment() {
      /** OPEN ROOM or NOT */
      this.$emit("add-slot", this.period);
    },
    onResumeMakeAppointment() {
      this.$emit("resume-booking", this.period);
    },
    async openAppointmentDetails() {
      if (!this.period.appointment.user) {
        this.rescheduleOpened = true;
        return;
      }

      this.appointmentLoading = true;
      this.appointmentDetails = null;
      this.detailsOpened = true;
      if (!this.period.appointment.status.includes("booking")) {
        this.appointmentDetails = {
          ...this.period.appointment,
          upcoming_appointments: []
        };
        const clientId = this.period.appointment.client_id;
        const params = {
          url: `/api/v1/clients/${clientId}/upcoming_appointments`
        };
        const { success, data } = await this.actionGet(params);
        this.appointmentLoading = false;
        if (!success) return;
        this.appointmentDetails.upcoming_appointments = data;
      }
      if (this.period.appointment.status.includes("booking")) {
        const bookingId = this.period.appointment.booking_appointment_id;
        const params = {
          url: `/api/v1/booking_appointments/${bookingId}`
        };
        const { success, data } = await this.actionGet(params);
        this.appointmentLoading = false;
        if (!success) return;
        this.appointmentDetails = data;
      }
    },
    closeHandler(e) {
      let close = true;
      if (e.target && e.target.dataset && e.target.dataset.id) {
        const id = e.target.dataset.id || "";
        if (id === this.dataId) close = false;
      }
      if (close) {
        window.removeEventListener("click", this.closeHandler);
        this.detailsOpened = false;
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/appointments/timeslot/Period.vue